import cns from 'classnames';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { RuntimeContext } from '../../../../../common/RuntimeContext';
import ToggleInput from '../../../../shared/components/formComponents/inputs/ToggleInput';
import VerbButton, { FONT_AWESOME_ICON_WEIGHT } from '../../../../shared/components/VerbButton';
import VerbHeading from '../../../../shared/components/VerbHeading';
import VerbParagraph from '../../../../shared/components/VerbParagraph';

const SuppressEmailContainer = ({
    updateEmailConfiguration,
    configurations = {},
    companyConfigurations = {},
    enableSaveButton,
    slackId,
    loggedInUserId,
    slackNotifications
}) => {
    const [showSave, setShowSave] = useState(false);
    const [saveDisabled, setSaveDisabled] = useState(false);
    const [formDirty, setFormDirty] = useState(false);
    const config = useContext(RuntimeContext);
    const updateConfig = (field) => (value) =>
        updateEmailConfiguration(field)(value).then(() => {
            setFormDirty(true);
        });

    const _renderSlackButton = () => {
        const slackUrl = `${config.pathsWeb.SLACK_URL}&state=${loggedInUserId}&redirect_uri=${encodeURI(
            `${config.pathsWeb.clientUrl}/slack_callback`
        )}`;

        let buttonId = 'ADD_TO_SLACK';
        let buttonText = 'ADD TO SLACK';
        let buttonIcon = 'fab fa-slack';
        let buttonIconWeight = FONT_AWESOME_ICON_WEIGHT.BRAND;
        let buttonClass = 'slack-btn';
        let buttonClick = () => {
            window.location.replace(slackUrl);
        };

        if (slackId.length && slackNotifications) {
            buttonId = 'REMOVE_SLACK';
            buttonText = 'REMOVE SLACK';
            buttonIcon = '';
            buttonIconWeight = FONT_AWESOME_ICON_WEIGHT.REGULAR;
            buttonClass = '';
            buttonClick = () =>
                window.open(
                    'https://slack.com/help/articles/360003125231-Remove-apps-and-custom-integrations-from-your-workspace#remove-an-app'
                );
        }

        return (
            <div className={'slack-button'}>
                <VerbButton
                    textId={buttonId}
                    defaultMessage={buttonText}
                    className={buttonClass}
                    fontAwesomeIcon={buttonIcon}
                    fontAwesomeIconWeight={buttonIconWeight}
                    onClick={buttonClick}
                />
            </div>
        );
    };

    const saveToggles = () => {
        setSaveDisabled(true);
        setShowSave(false);
        setTimeout(() => {
            setShowSave(true);
            setSaveDisabled(false);
        }, 500);
    };
    const togglesAvailable =
        companyConfigurations.teamLeadRecommendation ||
        companyConfigurations.teamLeadAnnoucement ||
        companyConfigurations.teamActivity ||
        companyConfigurations.teamFeedPost ||
        companyConfigurations.collectionReminder ||
        companyConfigurations.discover ||
        companyConfigurations.newContent ||
        companyConfigurations.peerRecommendation ||
        companyConfigurations.dueDateReminders;
    const teamEmailToggleAvailable =
        companyConfigurations.teamLeadRecommendation ||
        companyConfigurations.teamLeadAnnoucement ||
        companyConfigurations.teamActivity ||
        companyConfigurations.teamFeedPost;
    return (
        <div className={'col-12 email-settings'}>
            <div className={'row'}>
                <VerbHeading
                    className={'col-12 email-settings-heading'}
                    hNumber={2}
                    defaultMessage={'Notification Settings'}
                    messageId={'NOTIFICATION_SETTINGS_HEADING'}
                    BRAND
                />
                {togglesAvailable ? (
                    <VerbParagraph
                        className={'col-12 email-settings-sub-heading'}
                        defaultMessage={'Affects both email and Slack notifications.'}
                        messageId={'EMAIL_SETTINGS_SUB_HEADING'}
                    />
                ) : (
                    <VerbParagraph
                        className={'col-12 email-settings-sub-heading'}
                        defaultMessage={
                            'Your company admin has disabled all optional notifications. You will still receive essential system notifications like password resets, maintenance notices, etc.'
                        }
                        messageId={'EMAIL_SETTINGS_UNAVAILABLE_SUB_HEADING'}
                    />
                )}
                <div className={'col-10 col-xs-12'}>
                    {(companyConfigurations.collectionReminder || companyConfigurations.dueDateReminders) && (
                        <div className={'row toggle-row'}>
                            <div className={'col-1'}>
                                <ToggleInput
                                    input={{
                                        value: configurations.reminderEmails,
                                        onChange: updateConfig('reminderEmails')
                                    }}
                                    meta={{}}
                                    id={'collection-reminder-toggle'}
                                />
                            </div>
                            <div className={'col-11'}>
                                <div className={'row'}>
                                    <VerbParagraph
                                        className={'col-12 email-setting-title'}
                                        defaultMessage={'Reminder Notifications'}
                                        messageId={'EMAIL_SETTINGS_REMINDER_TITLE'}
                                    />
                                    <VerbParagraph
                                        className={'col-12 email-setting-description'}
                                        defaultMessage={
                                            'Reminders for upcoming due dates and unfinished content to keep you on track.'
                                        }
                                        messageId={'EMAIL_SETTINGS_REMINDER_DESCRIPTION'}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {teamEmailToggleAvailable && (
                        <div className={'row toggle-row'}>
                            <div className={'col-1'}>
                                <ToggleInput
                                    input={{
                                        value: configurations.teamUpdateEmails,
                                        onChange: updateConfig('teamUpdateEmails')
                                    }}
                                    meta={{}}
                                    id={'team-email-toggle'}
                                />
                            </div>
                            <div className={'col-11'}>
                                <div className={'row'}>
                                    <VerbParagraph
                                        className={'col-12 email-setting-title'}
                                        defaultMessage={'Team Activity Updates'}
                                        messageId={'EMAIL_SETTINGS_TEAM_ACTIVITY_TITLE'}
                                    />
                                    <VerbParagraph
                                        className={'col-12 email-setting-description'}
                                        defaultMessage={
                                            'Keep up with team conversations, announcements, recommendations, and assignments.'
                                        }
                                        messageId={'EMAIL_SETTINGS_TEAM_ACTIVITY_DESCRIPTION'}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {(companyConfigurations.discover || companyConfigurations.newContent) && (
                        <div className={'row toggle-row'}>
                            <div className={'col-1'}>
                                <ToggleInput
                                    input={{
                                        value: configurations.contentSuggestionEmails,
                                        onChange: updateConfig('contentSuggestionEmails')
                                    }}
                                    meta={{}}
                                    id={'content-suggestion-email-toggle'}
                                />
                            </div>
                            <div className={'col-11'}>
                                <div className={'row'}>
                                    <VerbParagraph
                                        className={'col-12 email-setting-title'}
                                        defaultMessage={'Content Suggestions'}
                                        messageId={'EMAIL_SETTINGS_CONTENT_SUGGESTIONS_TITLE'}
                                    />
                                    <VerbParagraph
                                        className={'col-12 email-setting-description'}
                                        defaultMessage={
                                            'Receive alerts about new releases and other content relevant to you.'
                                        }
                                        messageId={'EMAIL_SETTINGS_CONTENT_SUGGESTIONS_DESCRIPTION'}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {companyConfigurations.peerRecommendation && (
                        <div className={'row toggle-row'}>
                            <div className={'col-1'}>
                                <ToggleInput
                                    input={{
                                        value: configurations.recommendationEmails,
                                        onChange: updateConfig('recommendationEmails')
                                    }}
                                    meta={{}}
                                    id={'recommendations-email-toggle'}
                                />
                            </div>
                            <div className={'col-11'}>
                                <div className={'row'}>
                                    <VerbParagraph
                                        className={'col-12 email-setting-title'}
                                        defaultMessage={'Recommendations from Co-workers'}
                                        messageId={'EMAIL_SETTINGS_RECOMMENDATIONS_TITLE'}
                                    />
                                    <VerbParagraph
                                        className={'col-12 email-setting-description'}
                                        defaultMessage={'Know when a co-worker recommends a collection to you.'}
                                        messageId={'EMAIL_SETTINGS_RECOMMENDATIONS_DESCRIPTION'}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className={'slack-settings-container'}>
                    <VerbHeading
                        className={'col-12 email-settings-heading'}
                        hNumber={2}
                        defaultMessage={
                            slackNotifications && slackId.length ? 'Slack Notifications' : 'Enable Slack Notifications'
                        }
                        messageId={'NOTIFICATION_SLACK_HEADING'}
                    />
                    <div className={'row slack-button-row'}>
                        {_renderSlackButton()}
                        <VerbParagraph
                            className={'email-settings-sub-heading'}
                            defaultMessage={
                                slackNotifications && slackId.length
                                    ? 'Slack is already enabled for your account. (Visit your company \n workspace if you wish to remove Verb from your account.)'
                                    : 'Add Verb to Slack to receive reminders about content, \n upcoming due dates, team activity, and more.'
                            }
                            messageId={'EMAIL_SETTINGS_UNAVAILABLE_SUB_HEADING'}
                        />
                    </div>
                </div>
                <div className={cns('col-12', { hidden: !enableSaveButton })}>
                    <div className={'row'}>
                        <div className={'col-12'}>
                            <div className={'row'}>
                                <VerbButton
                                    className={'col-lg-3 col-md-4 col-xs-12'}
                                    disabled={!formDirty || saveDisabled}
                                    onClick={saveToggles}
                                >
                                    SAVE
                                </VerbButton>
                            </div>
                        </div>
                        {showSave && <div className={'col-12 success'}>Success!</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

SuppressEmailContainer.propTypes = {
    updateEmailConfiguration: PropTypes.object,
    configurations: PropTypes.object,
    companyConfigurations: PropTypes.object,
    enableSaveButton: PropTypes.bool,
    slackId: PropTypes.string,
    loggedInUserId: PropTypes.string,
    slackNotifications: PropTypes.bool
};

export default SuppressEmailContainer;
